import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, UserCheck, AlertCircle,
  BookOpen, Newspaper, ClipboardList, ChevronLeft, ChevronRight,
  GraduationCap, LogOut, ScanEye, Settings, BarChart2, Link2, Sunrise, TrendingUp,
  BookOpenCheck, CalendarDays, CalendarOff, MessageSquare, Award, Shield, FileText, Bus,
  ChevronDown,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { schoolConfigAPI } from '../../services/api';

// Groups render in this order; a group header only appears when the
// group has at least one item visible for the logged-in role.
const navGroups = [
  {
    label: null, // Dashboard stands alone, no header
    items: [
      { path: '/',                    label: 'Dashboard',            icon: LayoutDashboard, exact: true, module: 'dashboard' },
    ],
  },
  {
    label: 'Academics',
    items: [
      { path: '/timetable',           label: 'Timetable',             icon: CalendarDays,   module: 'timetable' },
      { path: '/lesson-plan',         label: 'Toddle Lesson Plan',    icon: ClipboardList,  module: 'lesson_plan' },
      { path: '/lesson-planner',      label: 'Toddle Planner',        icon: BookOpenCheck,  module: 'toddle_planner' },
      { path: '/homework',            label: 'Homework',              icon: BookOpen,       module: 'homework' },
      { path: '/learning-trackers',   label: 'Learning Trackers',     icon: BarChart2,      module: 'learning_trackers' },
      { path: '/morning-activity',    label: 'Morning Activity',      icon: Sunrise,        module: 'morning_activity' },
      { path: '/enrichment',          label: 'Enrichment Programme',  icon: Award,          module: 'enrichment_programme' },
    ],
  },
  {
    label: 'Employee & HR',
    items: [
      { path: '/attendance',          label: 'Employee Attendance',   icon: UserCheck,      module: 'attendance' },
      { path: '/leave',               label: 'Leave Management',      icon: CalendarOff,    module: 'leave_management' },
    ],
  },
  {
    label: 'Communication',
    items: [
      { path: '/ptc',                 label: 'Parent Teacher Connect', icon: MessageSquare, module: 'ptc' },
      { path: '/grievances',          label: 'Grievances',             icon: AlertCircle,   module: 'grievances' },
      { path: '/newsletter',          label: 'Newsletter',             icon: Newspaper,     module: 'newsletter' },
    ],
  },
  {
    label: 'Insights',
    items: [
      { path: '/teacher-observation', label: 'Teacher Observations',  icon: ScanEye,        module: 'teacher_observation' },
      { path: '/performance',         label: 'Performance Analytics', icon: TrendingUp,     module: 'performance_analytics' },
      { path: '/reports',             label: 'Reports',               icon: FileText,       module: 'reports' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { path: '/transport',           label: 'Transport Management',  icon: Bus,            module: 'transport' },
    ],
  },
  {
    label: 'Administration',
    items: [
      { path: '/erp-sync',            label: 'ERP Attendance Sync',   icon: Link2,    adminOnly: true, module: 'attendance' },
      { path: '/school-config',       label: 'School Configuration',  icon: Settings, adminOnly: true, module: 'school_config' },
    ],
  },
];

const BASE_URL = 'http://localhost:5000';

export default function Sidebar({ collapsed, onToggle }) {
  const { user, logout, hasModule } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const location = useLocation();
  const [schoolLogo, setSchoolLogo]   = useState(null);
  const [schoolName, setSchoolName]   = useState('The Kulish School');
  // Solo accordion: only one group open at a time, persisted per browser
  const [openGroup, setOpenGroup] = useState(
    () => localStorage.getItem('sidebar_open_group') || 'Academics'
  );

  const toggleGroup = label =>
    setOpenGroup(prev => {
      const next = prev === label ? null : label;
      localStorage.setItem('sidebar_open_group', next ?? '');
      return next;
    });

  // Auto-expand the group containing the current page
  useEffect(() => {
    const grp = navGroups.find(g =>
      g.label && g.items.some(i =>
        i.exact ? location.pathname === i.path : location.pathname.startsWith(i.path)
      )
    );
    if (grp) {
      setOpenGroup(grp.label);
      localStorage.setItem('sidebar_open_group', grp.label);
    }
  }, [location.pathname]);

  useEffect(() => {
    schoolConfigAPI.getConfig()
      .then(r => {
        if (r.data.school_logo) setSchoolLogo(`${BASE_URL}${r.data.school_logo}`);
        if (r.data.school_name) setSchoolName(r.data.school_name);
      })
      .catch(() => {});
  }, []);

  return (
    <aside
      className={`
        fixed top-0 left-0 h-full z-30 flex flex-col
        bg-gradient-to-b from-primary-900 to-primary-800
        transition-all duration-300 ease-in-out shadow-xl
        ${collapsed ? 'w-[72px]' : 'w-[260px]'}
      `}
    >
      {/* Logo & Title */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-primary-700/50">
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shadow-inner overflow-hidden">
          {schoolLogo
            ? <img src={schoolLogo} alt="School Logo" className="w-full h-full object-contain p-0.5"
                onError={() => setSchoolLogo(null)} />
            : <GraduationCap className="text-white" size={22} />
          }
        </div>
        {!collapsed && (
          <div className="overflow-hidden">
            <p className="text-white font-display font-bold text-sm leading-tight truncate">{schoolName}</p>
            <p className="text-primary-300 text-xs truncate">Kulish Suite</p>
          </div>
        )}
      </div>

      {/* Nav Items */}
      <nav className="flex-1 px-2 py-4 overflow-y-auto">
        {navGroups.map(({ label: groupLabel, items }) => {
          const visible = items.filter(item => {
            if (item.superAdminOnly && !isSuperAdmin) return false;
            if (item.adminOnly && !isSuperAdmin) return false;
            return hasModule(item.module);
          });
          if (!visible.length) return null;
          const open = !groupLabel || openGroup === groupLabel;
          const highlight = groupLabel && open && !collapsed;
          return (
            <div
              key={groupLabel ?? 'top'}
              className={`
                mb-1 rounded-xl border-l-[3px] transition-colors duration-300
                ${highlight ? 'bg-white/5 border-sky-400' : 'border-transparent'}
              `}
            >
              {groupLabel && (
                collapsed
                  ? <div className="mx-3 my-3 border-t border-primary-700/60" />
                  : <button
                      onClick={() => toggleGroup(groupLabel)}
                      className={`
                        w-full flex items-center justify-between px-3 pt-3 pb-1.5
                        text-xs font-bold uppercase tracking-wider transition-colors
                        ${open ? 'text-white' : 'text-primary-200 hover:text-white'}
                      `}
                    >
                      <span className="truncate">{groupLabel}</span>
                      <ChevronDown
                        size={14}
                        className={`flex-shrink-0 transition-transform duration-300 ${open ? '' : '-rotate-90'}`}
                      />
                    </button>
              )}
              <div
                className="overflow-hidden transition-[max-height] duration-300 ease-in-out space-y-1"
                style={{ maxHeight: collapsed || open ? '600px' : '0px' }}
              >
              {visible.map(({ path, label, icon: Icon, exact }) => {
                const active = exact ? location.pathname === path : location.pathname.startsWith(path);
                return (
                  <NavLink
                    key={path}
                    to={path}
                    title={collapsed ? label : undefined}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded-xl
                      transition-all duration-200 group relative
                      ${active
                        ? 'bg-white/15 text-white shadow-sm'
                        : 'text-primary-300 hover:bg-white/10 hover:text-white'
                      }
                    `}
                  >
                    {active && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white rounded-r-full" />
                    )}
                    <Icon size={20} className={`flex-shrink-0 transition-colors ${active ? 'text-white' : 'group-hover:text-white'}`} />
                    {!collapsed && (
                      <span className="text-sm font-medium truncate">{label}</span>
                    )}
                  </NavLink>
                );
              })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* User & Logout */}
      <div className="px-2 py-3 border-t border-primary-700/50 space-y-1">
        {!collapsed && (
          <div className="px-3 py-2 mb-1">
            <p className="text-white text-xs font-semibold truncate">{user?.name}</p>
            <p className="text-primary-300 text-xs capitalize truncate">{user?.role}</p>
          </div>
        )}
        <button
          onClick={logout}
          title={collapsed ? 'Logout' : undefined}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-primary-300 hover:bg-red-500/20 hover:text-red-300 transition-all duration-200"
        >
          <LogOut size={20} className="flex-shrink-0" />
          {!collapsed && <span className="text-sm font-medium">Logout</span>}
        </button>
      </div>

      {/* Collapse Toggle */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-primary-700 border-2 border-primary-500 flex items-center justify-center text-white hover:bg-primary-600 transition-colors shadow-md"
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}
